import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { API_BASE_URL } from '../lib/config';

type PanelType = 'dashboard' | 'orders' | 'transactions' | 'wallets' | 'settings';

interface OrderFilters {
    status?: string;
    network?: string;
    search?: string;
}

interface TransactionFilters {
    type?: string;
    network?: string;
}

interface AdminStore {
    // Auth
    token: string | null;
    isAuthenticated: boolean;

    // Navigation
    activePanel: PanelType;

    // Filters (persist across panel switches)
    orderFilters: OrderFilters;
    transactionFilters: TransactionFilters;

    // Actions
    login: (password: string) => Promise<void>;
    logout: () => void;
    setActivePanel: (panel: PanelType) => void;
    setOrderFilters: (filters: OrderFilters) => void;
    setTransactionFilters: (filters: TransactionFilters) => void;
    setToken: (token: string) => void;
}

export const useAdminStore = create<AdminStore>()(
    persist(
        (set) => ({
            // Initial state
            token: null,
            isAuthenticated: false,
            activePanel: 'dashboard',
            orderFilters: {},
            transactionFilters: {},

            // Login
            login: async (password: string) => {
                try {
                    const baseUrl = API_BASE_URL;
                    const response = await fetch(`${baseUrl}/auth/login`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ password }),
                    });

                    if (!response.ok) {
                        throw new Error('Invalid credentials');
                    }

                    const data = await response.json();
                    set({
                        token: data.access_token,
                        isAuthenticated: true,
                    });
                } catch (error) {
                    console.error('Login failed:', error);
                    throw error;
                }
            },

            // Logout
            logout: () => {
                set({
                    token: null,
                    isAuthenticated: false,
                    activePanel: 'dashboard',
                    orderFilters: {},
                    transactionFilters: {},
                });
            },

            // Set token (for manual token setting)
            setToken: (token: string) => {
                set({
                    token,
                    isAuthenticated: true,
                });
            },

            // Navigation
            setActivePanel: (panel: PanelType) => {
                set({ activePanel: panel });
            },

            // Filters
            setOrderFilters: (filters: OrderFilters) => {
                set({ orderFilters: filters });
            },

            setTransactionFilters: (filters: TransactionFilters) => {
                set({ transactionFilters: filters });
            },
        }),
        {
            name: 'admin-storage',
            partialize: (state) => ({
                token: state.token,
                isAuthenticated: state.isAuthenticated,
            }),
        }
    )
);
